export type PaymentMethodName = "BACs" | "Cash" | "Card"

/** Order the methods are listed in when a payment is split between several of them. */
export const PAYMENT_METHOD_NAMES: PaymentMethodName[] = ["BACs", "Cash", "Card"]

export interface IPaymentTypeFields {
  paymentMethod?: string | null
  paymentBacsPennies?: number | null
  paymentCashPennies?: number | null
  paymentCardPennies?: number | null
}

const AMOUNT_FIELDS = {
  BACs: "paymentBacsPennies",
  Cash: "paymentCashPennies",
  Card: "paymentCardPennies",
} as const

export function paymentAmountField(method: PaymentMethodName) {
  return AMOUNT_FIELDS[method]
}

/** The methods in a paymentMethod, "Cash & Card" is [Cash, Card] and "Card" is [Card]. */
export function paymentMethodParts(paymentMethod?: string | null): PaymentMethodName[] {
  const parts = (paymentMethod ?? "").split(" & ")
  return PAYMENT_METHOD_NAMES.filter(name => parts.includes(name))
}

/** paymentMethod saved for a payment made with these methods, e.g. "BACs & Cash". */
export function joinPaymentMethods(methods: PaymentMethodName[]) {
  return PAYMENT_METHOD_NAMES.filter(name => methods.includes(name)).join(" & ")
}

/** True when the payment is spread over two or more methods. */
export function isSplitPaymentMethod(paymentMethod?: string | null) {
  return paymentMethodParts(paymentMethod).length > 1
}

function formatMoney(pennies: number, symbol: string) {
  return `${symbol}${(pennies / 100).toFixed(2)}`
}

export function isSplitPayment(payment: IPaymentTypeFields) {
  const parts = paymentMethodParts(payment.paymentMethod)
  return parts.length > 1 && parts.every(name => payment[AMOUNT_FIELDS[name]] != null)
}

/** The payment type for display, with every amount for a split payment ("Cash £200.00 / Card £300.00"). */
export function formatPaymentType(payment: IPaymentTypeFields, symbol = "£") {
  if (isSplitPayment(payment)) {
    return paymentMethodParts(payment.paymentMethod)
      .map(name => `${name} ${formatMoney(payment[AMOUNT_FIELDS[name]]!, symbol)}`)
      .join(" / ")
  }
  return payment.paymentMethod ?? ""
}

export interface IPaymentTotals {
  cash: number
  card: number
  bacs: number
}

/** Adds up what was paid by each method across payments of the given totals (in pennies). */
export function totalPaymentsByMethod(
  payments: (IPaymentTypeFields & { totalPennies: number })[],
): IPaymentTotals {
  const totals: IPaymentTotals = { cash: 0, card: 0, bacs: 0 }
  for (const payment of payments) {
    if (isSplitPayment(payment)) {
      totals.bacs += payment.paymentBacsPennies ?? 0
      totals.cash += payment.paymentCashPennies ?? 0
      totals.card += payment.paymentCardPennies ?? 0
    } else if (payment.paymentMethod === "Cash") {
      totals.cash += payment.totalPennies
    } else if (payment.paymentMethod === "Card") {
      totals.card += payment.totalPennies
    } else if (payment.paymentMethod === "BACs") {
      totals.bacs += payment.totalPennies
    }
  }
  return totals
}
